import httpService from "V2.0/services/httpService";
import { EvalRunConfigT } from "./types";

export {
  getEvalRuns,
  getEvalRun,
  postCreateEvalRun,
  deleteEvalRun,
  getEvalTemplates,
  postCreateEvalTemplate,
  putUpdateEvalTemplate,
  patchEvalTemplateActive,
  getEvalTemplateCopyCandidates,
  postCopyEvalTemplate,
};

const routes = {
  listEvalRuns: "/eval/whitelabelRuns",
  getEvalRun: "/eval/whitelabelRun",
  createEvalRun: "/eval/whitelabelCreateEvalRun",
  deleteEvalRun: "/eval/whitelabelRun",
  templates: "/eval/whitelabelTemplates",
  template: "/eval/whitelabelTemplate",
  templateActive: "/eval/whitelabelTemplateActive",
  templateCopyCandidates: "/eval/whitelabelTemplateCopyCandidates",
  copyTemplate: "/eval/whitelabelCopyTemplate",
} as const;

interface GetEvalRunsRequest {
  params: {
    workspaceId: string;
    assistantId: string;
  };
  queryParams: {
    page: number;
    limit: number;
  };
}

function getEvalRuns(request: GetEvalRunsRequest) {
  const { params, queryParams } = request;
  return httpService.get(
    `${routes.listEvalRuns}/${params.workspaceId}/${params.assistantId}`,
    {
      params: queryParams,
    },
  );
}

interface GetEvalRunRequest {
  params: { workspaceId: string; evalRunId: string };
}

function getEvalRun(request: GetEvalRunRequest) {
  const { params } = request;

  return httpService.get(
    `${routes.getEvalRun}/${params.workspaceId}/${params.evalRunId}`,
  );
}

interface CreateEvalRunRequest {
  params: { workspaceId: string; assistantId: string };
  body: { config: Partial<EvalRunConfigT> };
}

function postCreateEvalRun(request: CreateEvalRunRequest) {
  const { params, body } = request;
  return httpService.post(
    `${routes.createEvalRun}/${params.workspaceId}/${params.assistantId}`,
    body,
  );
}

interface DeleteEvalRunRequest {
  params: { workspaceId: string; evalRunId: string };
}

function deleteEvalRun(request: DeleteEvalRunRequest) {
  const { params } = request;

  return httpService.delete(
    `${routes.deleteEvalRun}/${params.workspaceId}/${params.evalRunId}`,
  );
}

interface GetEvalTemplatesRequest {
  params: { workspaceId: string; assistantId: string };
  queryParams?: { page?: number; limit?: number; activeOnly?: boolean };
}

function getEvalTemplates(request: GetEvalTemplatesRequest) {
  const { params, queryParams } = request;

  return httpService.get(
    `${routes.templates}/${params.workspaceId}/${params.assistantId}`,
    { params: queryParams },
  );
}

interface CreateEvalTemplateRequest {
  params: { workspaceId: string; assistantId: string };
  body: { name?: string; description?: string; config: Partial<EvalRunConfigT> };
}

function postCreateEvalTemplate(request: CreateEvalTemplateRequest) {
  const { params, body } = request;

  return httpService.post(
    `${routes.templates}/${params.workspaceId}/${params.assistantId}`,
    body,
  );
}

interface UpdateEvalTemplateRequest {
  params: { workspaceId: string; templateId: string };
  body: { name?: string; description?: string; config?: Partial<EvalRunConfigT> };
}

function putUpdateEvalTemplate(request: UpdateEvalTemplateRequest) {
  const { params, body } = request;

  return httpService.put(
    `${routes.template}/${params.workspaceId}/${params.templateId}`,
    body,
  );
}

interface SetEvalTemplateActiveRequest {
  params: { workspaceId: string; templateId: string };
  body: { isActive: boolean };
}

function patchEvalTemplateActive(request: SetEvalTemplateActiveRequest) {
  const { params, body } = request;

  return httpService.patch(
    `${routes.templateActive}/${params.workspaceId}/${params.templateId}`,
    body,
  );
}

interface GetCopyCandidatesRequest {
  params: { workspaceId: string; assistantId: string };
}

/** Templates on the workspace's OTHER assistants, to pick one to copy in. */
function getEvalTemplateCopyCandidates(request: GetCopyCandidatesRequest) {
  const { params } = request;

  return httpService.get(
    `${routes.templateCopyCandidates}/${params.workspaceId}/${params.assistantId}`,
  );
}

interface CopyEvalTemplateRequest {
  params: { workspaceId: string; assistantId: string };
  body: { sourceTemplateId: string; name?: string };
}

function postCopyEvalTemplate(request: CopyEvalTemplateRequest) {
  const { params, body } = request;

  return httpService.post(
    `${routes.copyTemplate}/${params.workspaceId}/${params.assistantId}`,
    body,
  );
}
